import { z } from "zod";

import {
  CARD_VARIABLE_TYPES,
  CardVariableSchema
} from "../../../../schemas/AccessCard/cardVariable.schema";
import {
  VALUE_CONSTRAINT_FORMATS,
  ValueConstraintSchema
} from "../../../../schemas/AccessCard/valueConstraint.schema";
import { enumReadSchema } from "../../../../shared/core/enum-read.schema";

// ----------------------------------------------------------------------------
// Grantable cards (the import picker)
// ----------------------------------------------------------------------------

/**
 * A card the console caller may import into this app.
 *
 * Master cards are excluded SERVER-SIDE rather than flagged for the client to
 * hide: a master card passes every action through unattenuated, so offering
 * one and refusing it later would put the refusal after the choice. Cards the
 * caller cannot reach are absent for the same reason existence is not
 * learnable on the import route.
 *
 * Carries no credential value and no policy document — a count of permitted
 * actions, and the variables a consumer will have to supply.
 */
/**
 * A card variable as the grantable-cards list's READERS parse it: its `type` and
 * its constraint's `format` read leniently, so a newer backend's variable type or
 * format reaches an older console as an unlisted value instead of failing the
 * picker. The access-card schemas stay strict for the card's own writers, and the
 * array-level "names are unique" check is theirs too — a reader has nothing to
 * enforce there.
 */
const GrantableCardVariableReadSchema = CardVariableSchema.extend({
  type: enumReadSchema(CARD_VARIABLE_TYPES).default("string"),
  constraint: ValueConstraintSchema.extend({
    format: enumReadSchema(VALUE_CONSTRAINT_FORMATS).optional()
  }).optional()
});

export const GrantableAccessCardSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  color: z.string(),
  credentialId: z.string().uuid(),
  /** Display name of the credential this card attenuates. */
  credentialName: z.string(),
  /** Logo of the service behind the credential, when the tool registry has one. */
  credentialImageUrl: z.string().nullable(),
  allowedActionCount: z.number().int().nonnegative(),
  variables: z.array(GrantableCardVariableReadSchema).default([]),
  /**
   * Already imported into THIS app. Still listed, because a card may be
   * imported under a second name for a second scope — the picker greys it and
   * says so rather than hiding a legitimate choice.
   */
  alreadyBound: z.boolean(),
  createdAt: z.coerce.date()
});

export type GrantableAccessCardDto = z.infer<typeof GrantableAccessCardSchema>;

export const ListGrantableAccessCardsPathVarsSchema = z.object({
  appId: z.string().uuid()
});

export const ListGrantableAccessCardsResponseSchema = z.object({
  cards: z.array(GrantableAccessCardSchema)
});
